import React from 'react';

import { graphql, PageProps } from 'gatsby';

import { Grid, Link, styled } from '@mui/material';

import { ResponsiveGrid } from '@mewa/ui';

import { marketsConfig } from '../../libs/config/markets';
import t from '../../libs/translate';
import { PageHeadlineWrapper } from '../components/page-headline-wrapper';
import { LayoutType } from '../layouts/types';

export { Head } from '../layouts/head';

export const query = graphql`
  query ($locale: String!) {
    translations: translation(locale: { eq: $locale }) {
      footer_copyright_note
    }
  }
`;

type Props = {
  translations: {
    footer_copyright_note: string;
  };
};

const LandingPage = ({ data: { translations } }: PageProps<Props>) => {
  const allMarkets = Object.values(marketsConfig)
    .map((market) =>
      market.languages.map((l) => ({
        name: l.localizedMarketName.replace('Mewa', '').trim(),
        link:
          l.domain ??
          market.domain + (market.languages.length > 1 ? `/${l.code}/` : ''),
      })),
    )
    .sort((a, b) => a[0].name.localeCompare(b[0].name));

  return (
    <div
      style={{
        minHeight: 'calc(100vh - var(--header-height))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <StyledPageHeadline
        title={'Discover Mewa'}
        largerMobile
        subtitle={'Please select your country'}
      />

      <ResponsiveGrid gridProps={{ sx: { height: '100%' } }}>
        {allMarkets.map((marketLanguages, i) => {
          return (
            <MarketGridItem
              key={i}
              item
              xs={12}
              sm={i % 2 === 0 ? 4 : 8}
              md={3}
            >
              {marketLanguages.map((market) => (
                <StyledLink
                  key={market.name}
                  href={market.link}
                  underline="none"
                  color={'secondary'}
                >
                  {market.name}
                </StyledLink>
              ))}
            </MarketGridItem>
          );
        })}
      </ResponsiveGrid>

      <ResponsiveGrid sx={{ marginTop: 'auto' }}>
        <Grid item xs={12}>
          <Divider />
          <CopyrightNote>
            {t(translations, 'footer_copyright_note')}
          </CopyrightNote>
        </Grid>
      </ResponsiveGrid>
    </div>
  );
};

LandingPage.layout = LayoutType.Minimal;

export default LandingPage;

const StyledPageHeadline = styled(PageHeadlineWrapper)(({ theme }) => ({
  marginBottom: '77px',

  [theme.breakpoints.between('sm', 'md')]: {
    marginTop: '64px',
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: '106px',
  },
}));

const MarketGridItem = styled(Grid)(({ theme }) => ({
  marginBottom: '23px',

  [theme.breakpoints.between('sm', 'md')]: {
    marginBottom: '30px',
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  ...theme.typography.copy,
  marginRight: '14px',
}));

const CopyrightNote = styled('div')(({ theme }) => ({
  ...theme.typography.copySm,

  color: 'var(--color-medium-grey)',

  paddingTop: '20px',
  paddingBottom: '24px',

  [theme.breakpoints.up('sm')]: {
    paddingTop: '16px',
    paddingBottom: '30px',
  },

  [theme.breakpoints.up('md')]: {
    paddingTop: '38px',
    paddingBottom: '45px',
  },
}));

const Divider = styled('hr')({
  border: 'none',
  height: '1px',
  backgroundColor: 'var(--color-medium-grey)',
  opacity: 0.2,
  marginTop: '55px',
});
